import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

interface CenteredCoverImageProps {
  src?: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  focusX?: number;
  focusY?: number;
}

export const CenteredCoverImage = ({
  src,
  alt = '',
  className = '',
  style,
  focusX = 0.5,
  focusY = 0.5,
  ...rest
}: CenteredCoverImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const positionAndScaleImage = () => {
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image || !image.naturalWidth || !image.naturalHeight) return;

    const devicePixelRatioValue = window.devicePixelRatio || 1;
    const snapToDevicePixels = (value: number) => Math.round(value * devicePixelRatioValue) / devicePixelRatioValue;

    const containerWidth = snapToDevicePixels(container.clientWidth);
    const containerHeight = snapToDevicePixels(container.clientHeight);
    const imageNaturalWidth = image.naturalWidth;
    const imageNaturalHeight = image.naturalHeight;

    const containerAspectRatio = containerWidth / containerHeight;
    const imageAspectRatio = imageNaturalWidth / imageNaturalHeight;

    let renderWidth: number;
    let renderHeight: number;

    if (imageAspectRatio > containerAspectRatio) {
      renderHeight = containerHeight;
      renderWidth = snapToDevicePixels(containerHeight * imageAspectRatio);
    } else {
      renderWidth = containerWidth;
      renderHeight = snapToDevicePixels(containerWidth / imageAspectRatio);
    }

    const offsetLeft = -(focusX * renderWidth) + containerWidth * 0.5;
    const offsetTop = -(focusY * renderHeight) + containerHeight * 0.5;

    Object.assign(image.style, {
      position: 'absolute',
      width: `${renderWidth}px`,
      height: `${renderHeight}px`,
      left: `${offsetLeft}px`,
      top: `${offsetTop}px`,
      transform: 'translate(0, 0)',
    });
  };

  useLayoutEffect(() => {
    positionAndScaleImage();
  });

  useEffect(() => {
    if (!isLoaded) return;
    positionAndScaleImage();
    const observer = new ResizeObserver(positionAndScaleImage);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  return (
    <div
      ref={containerRef}
      className={classNames('relative w-full overflow-hidden [aspect-ratio:16/9]', className)}
      style={style}>
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        onLoad={() => {
          setIsLoaded(true);
          positionAndScaleImage();
        }}
        style={{ visibility: isLoaded ? 'visible' : 'hidden' }}
        {...rest}
      />
      {!isLoaded && <div className="absolute inset-0 h-full w-full animate-pulse bg-gray-200" />}
    </div>
  );
};
